import React, { useEffect, useState } from "react";
import { api, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Send } from "lucide-react";

const AUDIENCE_LABELS = { all: "all learners", employees: "employees only", dealers: "dealers only" };

/** Post-publish prompt: email learners the moment a course (learner-facing "module") goes live. */
export const CourseAnnounceDialog = ({ open, onOpenChange, course }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("employees");
  const [counts, setCounts] = useState({ employees: 0, dealers: 0, all: 0 });
  const [sending, setSending] = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    if (!open || !course) return;
    setSubject(`Now live: ${course.title}`);
    setBody(`Hello,

A new module — "${course.title}" — has just gone live on the UniVicoustic Learning Academy.

Log in to start it now; we recommend going through it at your own pace.

— UniVicoustic Learning Team`);
    setAudience("employees");
    api.get("/learners").then(({ data }) => {
      const employees = data.filter((l) => (l.audience || "employee") !== "dealer").length;
      setCounts({ employees, dealers: data.length - employees, all: data.length });
    }).catch(() => {});
  }, [open, course]);

  const send = async (testOnly) => {
    setSending(testOnly ? "test" : "all");
    try {
      const payload = {
        subject, body, audience,
        link_path: `/learn/courses/${course.id}`,
        cta_label: "Open the new module",
      };
      if (testOnly) payload.test_email = getUser()?.email;
      const { data } = await api.post("/announcements/send-custom", payload);
      if (testOnly) {
        toast.success(`Test email sent to ${getUser()?.email}`);
      } else {
        toast.success(`Announcement sent to ${data.sent} of ${data.learner_count} recipients`);
        onOpenChange(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the announcement.");
    } finally {
      setSending(null);
    }
  };

  if (!course) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="course-announce-dialog">
        <DialogHeader>
          <DialogTitle>Module is live — tell your learners?</DialogTitle>
          <DialogDescription>
            &ldquo;{course.title}&rdquo; just went live. Send a branded email now, or close this to skip.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Audience</div>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger data-testid="course-announce-audience"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="employees" data-testid="course-announce-audience-employees">Employees only ({counts.employees})</SelectItem>
                <SelectItem value="dealers" data-testid="course-announce-audience-dealers">Dealers only ({counts.dealers})</SelectItem>
                <SelectItem value="all" data-testid="course-announce-audience-all">All learners ({counts.all})</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Subject</div>
            <Input
              data-testid="course-announce-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
          </div>
          <div>
            <div className="text-xs font-medium text-slate-600 mb-1">Message</div>
            <Textarea
              data-testid="course-announce-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
              maxLength={4000}
              className="text-sm"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" data-testid="course-announce-skip" disabled={!!sending} onClick={() => onOpenChange(false)}>
            Skip
          </Button>
          <Button
            variant="outline"
            data-testid="course-announce-test"
            disabled={!!sending || subject.length < 3 || body.length < 10}
            onClick={() => send(true)}
          >
            {sending === "test" ? "Sending…" : "Send me a test"}
          </Button>
          <Button
            data-testid="course-announce-send"
            disabled={!!sending || subject.length < 3 || body.length < 10}
            onClick={() => {
              if (!confirmStep) {
                setConfirmStep(true);
                setTimeout(() => setConfirmStep(false), 6000);
                return;
              }
              setConfirmStep(false);
              send(false);
            }}
            className={`${confirmStep ? "bg-[#8f1f1d] hover:bg-[#7a1a18]" : "bg-[#E53935] hover:bg-[#c62828]"} text-white`}
          >
            <Send size={14} className="mr-1.5" />
            {sending === "all"
              ? "Sending…"
              : confirmStep
                ? `Really email ${counts[audience]} recipients?`
                : `Send to ${AUDIENCE_LABELS[audience]}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CourseAnnounceDialog;
